import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Search, Book, Users, ArrowRightLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Library = () => {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState('');
  const [isAddBookOpen, setIsAddBookOpen] = useState(false);
  const { toast } = useToast();

  const [bookForm, setBookForm] = useState({
    title: '', author: '', isbn: '', publisher: '', category: '', quantity: '1',
  });

  useEffect(() => { fetchData(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchData = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const params = {
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const [booksRes, membersRes, issuesRes] = await Promise.all([
        api.get('/library/books', { params }),
        api.get('/library/members', { params }),
        api.get('/library/issues', { params }),
      ]);
      setBooks(booksRes.data.data || []);
      setMembers(membersRes.data.data || []);
      setIssues(issuesRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch library data');
    }
  };

  const handleAddBook = async (e) => {
    e.preventDefault();
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      await api.post('/library/books', {
        ...bookForm,
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      toast({ title: 'Success', description: 'Book added successfully' });
      setIsAddBookOpen(false);
      setBookForm({ title: '', author: '', isbn: '', publisher: '', category: '', quantity: '1' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add book', variant: 'destructive' });
    }
  };

  const filteredBooks = books.filter(b => 
    `${b.title} ${b.author} ${b.isbn}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Library</h1>
          <p className="text-slate-400">Manage books and members</p>
        </div>
        <Dialog open={isAddBookOpen} onOpenChange={setIsAddBookOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Add Book
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader><DialogTitle>Add New Book</DialogTitle></DialogHeader>
            <form onSubmit={handleAddBook} className="space-y-4">
              <div><Label>Title *</Label><Input value={bookForm.title} onChange={(e) => setBookForm({...bookForm, title: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Author</Label><Input value={bookForm.author} onChange={(e) => setBookForm({...bookForm, author: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>ISBN</Label><Input value={bookForm.isbn} onChange={(e) => setBookForm({...bookForm, isbn: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category</Label><Input value={bookForm.category} onChange={(e) => setBookForm({...bookForm, category: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Quantity</Label><Input type="number" value={bookForm.quantity} onChange={(e) => setBookForm({...bookForm, quantity: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Add Book</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Book className="w-6 h-6 text-blue-500" />
            </div>
            <div><p className="text-sm text-slate-400">Total Books</p><p className="text-2xl font-bold text-white">{books.length}</p></div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-green-500" />
            </div>
            <div><p className="text-sm text-slate-400">Members</p><p className="text-2xl font-bold text-white">{members.length}</p></div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <ArrowRightLeft className="w-6 h-6 text-amber-500" />
            </div>
            <div><p className="text-sm text-slate-400">Active Issues</p><p className="text-2xl font-bold text-white">{issues.filter(i => i.status === 'issued').length}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="books" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="books">Books</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="issues">Issues</TabsTrigger>
        </TabsList>

        <TabsContent value="books">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="relative"><Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input placeholder="Search books..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-700 border-slate-600 text-white" /></div>
            </CardHeader>
            <CardContent>
              {filteredBooks.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No books found</p>
              ) : (
                <div className="grid gap-4">
                  {filteredBooks.map(book => (
                    <div key={book.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-16 bg-blue-500/20 rounded flex items-center justify-center">
                          <Book className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{book.title}</h3>
                          <p className="text-sm text-slate-400">{book.author} • {book.category}</p>
                          <p className="text-xs text-slate-500">ISBN: {book.isbn || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{book.available_quantity}/{book.quantity}</p>
                        <p className="text-xs text-slate-400">Available</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              {members.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No members found</p>
              ) : (
                <div className="grid gap-4">
                  {members.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                          <span className="text-green-500 font-semibold">{member.first_name?.[0]}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{member.first_name} {member.last_name}</h3>
                          <p className="text-sm text-slate-400">{member.member_id}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${member.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {member.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issues">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              {issues.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No issues found</p>
              ) : (
                <div className="grid gap-4">
                  {issues.map(issue => (
                    <div key={issue.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <div>
                        <h3 className="font-semibold text-white">{issue.book_title}</h3>
                        <p className="text-sm text-slate-400">{issue.first_name} {issue.last_name} ({issue.member_id})</p>
                        <p className="text-xs text-slate-500">Due: {new Date(issue.due_date).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        issue.status === 'issued' ? 'bg-amber-500/20 text-amber-400' 
                        : issue.status === 'returned' ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                      }`}>
                        {issue.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Library;
